import { AspectRatio, Box, BoxProps, Button, Flex, Icon, Skeleton, Text, Image, Grid } from '@chakra-ui/react';
import { useResource, useResourceRelationship, useResources } from 'datx-jsonapi-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import LinkIcon from '@/assets/icons/ic-open-in-new.svg';
import PaperIcon from '@/assets/icons/ic-paper.svg';

import { useTrackOptions } from '@/components/ui/features/lecture/LecturePlayer/LecturePlayer.hooks';
import { LecturePremiumOverlayButton } from '@/components/ui/features/lecture/LecturePremiumOverlayButton/LecturePremiumOverlayButton';
import { useOriginalVideoEvents } from '@/components/ui/features/originals/EpisodesSection/components/EpisodeDetails.hooks';
import { useEpisodeContext } from '@/components/ui/features/originals/EpisodesSection/EpisodeSection.context';
import { VideoJS } from '@/components/ui/shared/video/VideoJS/VideoJS';
import { defaultVideoType } from '@/constants/video';
import { useAnalytics } from '@/context/Analytics';
import { PackageType } from '@/enums/Originals';
import { useQuery } from '@tanstack/react-query';

import { SlugIdParam, useQueryParams } from '@/hooks/useQueryParams';
import { queryLiblynxAuthorizationGrants } from '@/queries/liblynx-authorization-grants';
import { sessionQueryOptions } from '@/queries/upgrade/sessions';
import { queryEpisodeDetails, queryEpisodesForSeries, queryEpisodeTranscripts } from '@/queries/originals-episodes';
import { OriginalsEpisodeTranscripts } from '@/resources/OriginalsEpisodeTranscripts';

export const EpisodeDetails: FC<BoxProps> = (props) => {
  const { data: session } = useQuery(sessionQueryOptions());
  const { user } = session || {};
  const analytics = useAnalytics();
  const { data: libLynxData } = useResource(queryLiblynxAuthorizationGrants('auth_grant'));
  const { id: seriesId } = useQueryParams<{ id: string }>({
    id: SlugIdParam,
  });

  const { selectedEpisodeId } = useEpisodeContext();

  const { t } = useTranslation();
  const { data: episodes } = useResources(queryEpisodesForSeries(seriesId));

  const selectedEpisode = episodes?.find((e) => e.id === selectedEpisodeId) || episodes?.[0];

  const { data: transcripts, error: transcriptsError } = useResourceRelationship<Array<OriginalsEpisodeTranscripts>>(
    queryEpisodeTranscripts(selectedEpisode?.id)
  );

  const transcript = transcripts?.[0];

  const trackOptions = useTrackOptions({ transcripts, activeTrackId: transcript?.id });

  const { data, error } = useResourceRelationship(queryEpisodeDetails(selectedEpisode?.id));

  const videoEvents = useOriginalVideoEvents(selectedEpisode);

  const isLiblynxGrantActive = libLynxData?.status === 'active';
  const isUserPremium = session && user?.package === 'premium';
  const isVideoFree = selectedEpisode?.package === PackageType.FREE;

  const haveAccess = isUserPremium || isLiblynxGrantActive || isVideoFree;
  const FALLBACK_POSTER_PATH = '/images/landing/video-lectures.png';

  return (
    <Box {...props} w="full" mr={{ sm: 0, xl: 8 }}>
      <AspectRatio maxW="full" ratio={16 / 9} flexGrow={1}>
        <Skeleton isLoaded={Boolean((data && transcript) || error)}>
          {/* the tint is on the wrapper, not the image: the image is 30% opaque,
              so a background on it would be diluted to 30% as well */}
          {!haveAccess && (
            <Box position="relative" bg="gray.50">
              <Image
                opacity="0.3"
                src={selectedEpisode?.largeThumbnailUrl || FALLBACK_POSTER_PATH}
                alt={selectedEpisode?.title ?? ''}
                fallback={<Skeleton size="full" />}
              />

              <Grid w="full" h="full" placeItems="center" position="absolute" top="0" left="0">
                <LecturePremiumOverlayButton />
              </Grid>
            </Box>
          )}

          {haveAccess && (
            <VideoJS
              data-peer
              {...videoEvents}
              key={selectedEpisodeId + seriesId}
              title={selectedEpisode?.title}
              options={{
                sources: [{ src: data?.playlist, type: defaultVideoType }],
                tracks: trackOptions,
                poster: selectedEpisode?.largeThumbnailUrl,
              }}
              crossOrigin="anonymous"
            />
          )}
        </Skeleton>
      </AspectRatio>
      <Skeleton isLoaded={Boolean(selectedEpisode)}>
        <Text fontSize="2xl" my="1rem" fontWeight="bold">
          {selectedEpisode?.title}
        </Text>
      </Skeleton>
      <Skeleton isLoaded={Boolean(selectedEpisode)}>
        <Text fontSize="md">{selectedEpisode?.description}</Text>
      </Skeleton>
      {(selectedEpisode?.notesUrl || selectedEpisode?.readingsUrl) && (
        <Box w="1rem" borderBottom="2px solid black" my="2rem" />
      )}
      <Flex flexDir={{ base: 'column', md: 'row' }} alignItems={{ base: 'flex-start' }}>
        <Skeleton isLoaded={Boolean(selectedEpisode)}>
          {selectedEpisode?.notesUrl && (
            <Button
              as="a"
              onClick={() =>
                analytics.track('originals_lecture_notes_download', {
                  originals_episode: selectedEpisode?.title,
                  originals_episode_id: selectedEpisode?.id,
                })
              }
              target="_blank"
              rel="noopener noreferrer"
              href={selectedEpisode?.notesUrl}
              mr="2rem"
              variant="unstyled"
              display={selectedEpisode?.notesUrl ? 'flex' : 'none'}
              cursor="pointer"
              leftIcon={<Icon as={PaperIcon} w="24px" h="24px" color="black" />}
            >
              {t('episodeDetails.lectureNotes')}
            </Button>
          )}
        </Skeleton>
        <Skeleton isLoaded={Boolean(selectedEpisode)}>
          {selectedEpisode?.readingsUrl && (
            <Button
              as="a"
              onClick={() =>
                analytics.track('originals_further_readings_click', {
                  originals_episode: selectedEpisode?.title,
                  originals_episode_id: selectedEpisode?.id,
                  url: selectedEpisode?.readingsUrl,
                })
              }
              target="_blank"
              rel="noopener noreferrer"
              href={selectedEpisode?.readingsUrl}
              variant="unstyled"
              display={selectedEpisode?.readingsUrl ? 'flex' : 'none'}
              cursor="pointer"
              leftIcon={<Icon as={LinkIcon} w="24px" h="24px" color="black" />}
            >
              {t('episodeDetails.furtherReadings')}
            </Button>
          )}
        </Skeleton>
      </Flex>
    </Box>
  );
};
